/**
 * useElectronFileSystem — hook per navigazione filesystem quando si gira
 * dentro la shell Electron con preload.cjs attivo.
 *
 * Accede direttamente al percorso NAS tramite window.electronFS (Node.js fs
 * esposto via contextBridge), senza bisogno di File System Access API né
 * di permessi utente.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import type { FileSystemEntry } from './useFileSystem';

declare global {
  interface Window {
    electronDialog?: {
      pickDirectory: (defaultPath?: string) => Promise<string | null>;
    };
    isElectron?: boolean;
    electronAppVersion?: string;
    electronFS?: {
      readDirectory(dirPath: string): { name: string; kind: 'file' | 'directory'; size?: number; lastModified?: number }[];
      listDirectoryNames(dirPath: string): string[];
      directoryExists(dirPath: string): boolean;
      pathExists(p: string): boolean;
      openPath(filePath: string): Promise<string>;
      createDirectory(dirPath: string): boolean;
      deleteEntry(entryPath: string): boolean;
      writeFile(filePath: string, arrayBuffer: ArrayBuffer): boolean;
      readFile(filePath: string): ArrayBuffer;
      joinPath(...segments: string[]): string;
      extname(filePath: string): string;
    };
  }
}

export function isElectronEnvironment(): boolean {
  return !!(typeof window !== 'undefined' && window.isElectron && window.electronFS);
}

interface ElectronFSState {
  isSupported: boolean;
  hasPermission: boolean;
  rootHandle: null;
  currentPath: string[];
  entries: FileSystemEntry[];
  isLoading: boolean;
  error: string | null;
}

function getExtension(name: string): string {
  const parts = name.split('.');
  return parts.length > 1 ? (parts.pop()?.toLowerCase() || '') : '';
}

export function useElectronFileSystem(basePath: string | undefined) {
  const efs = window.electronFS!;
  const [state, setState] = useState<ElectronFSState>({
    isSupported: true,
    hasPermission: false,
    rootHandle: null,
    currentPath: [],
    entries: [],
    isLoading: false,
    error: null,
  });
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const prevBasePathRef = useRef<string | undefined>(undefined);

  const buildFullDir = useCallback((segments: string[]): string => {
    if (!basePath) return '';
    if (segments.length === 0) return basePath;
    return efs.joinPath(basePath, ...segments);
  }, [basePath, efs]);

  const readDir = useCallback((dirPath: string): FileSystemEntry[] => {
    const raw = efs.readDirectory(dirPath);
    const entries: FileSystemEntry[] = raw.map((e) => ({
      name: e.name,
      kind: e.kind,
      path: '', // verrà impostato sotto
      handle: null as unknown as FileSystemHandle,
      size: e.size,
      lastModified: e.lastModified,
      extension: e.kind === 'file' ? getExtension(e.name) : undefined,
    }));
    entries.sort((a, b) => {
      if (a.kind !== b.kind) return a.kind === 'directory' ? -1 : 1;
      return a.name.localeCompare(b.name, 'it');
    });
    return entries;
  }, [efs]);

  const updateEntries = useCallback((segments: string[]) => {
    const dirPath = buildFullDir(segments);
    if (!dirPath) return;
    try {
      const entries = readDir(dirPath);
      entries.forEach((e) => {
        e.path = [...segments, e.name].join('/');
      });
      setState((prev) => ({
        ...prev,
        currentPath: segments,
        entries,
        hasPermission: true,
        isLoading: false,
        error: null,
      }));
    } catch (err: any) {
      setState((prev) => ({
        ...prev,
        currentPath: segments,
        entries: [],
        isLoading: false,
        error: err.message || 'Errore nella lettura della cartella',
      }));
    }
  }, [buildFullDir, readDir]);

  useEffect(() => {
    if (basePath === prevBasePathRef.current) return;
    prevBasePathRef.current = basePath;

    if (!basePath) {
      setState((prev) => ({
        ...prev,
        hasPermission: false,
        currentPath: [],
        entries: [],
        error: null,
      }));
      setInitialLoadDone(true);
      return;
    }

    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      if (!efs.directoryExists(basePath)) {
        setState((prev) => ({
          ...prev,
          hasPermission: false,
          isLoading: false,
          error: `Il percorso "${basePath}" non è raggiungibile da questo computer.`,
          currentPath: [],
          entries: [],
        }));
        setInitialLoadDone(true);
        return;
      }
      updateEntries([]);
    } catch (err: any) {
      setState((prev) => ({
        ...prev,
        hasPermission: false,
        isLoading: false,
        error: err.message,
        currentPath: [],
        entries: [],
      }));
    }
    setInitialLoadDone(true);
  }, [basePath, efs, updateEntries]);

  const navigateTo = useCallback((path: string[]) => {
    setState((prev) => ({ ...prev, isLoading: true }));
    updateEntries(path);
  }, [updateEntries]);

  const navigateUp = useCallback(() => {
    if (state.currentPath.length === 0) return;
    navigateTo(state.currentPath.slice(0, -1));
  }, [state.currentPath, navigateTo]);

  const navigateToRoot = useCallback(() => {
    navigateTo([]);
  }, [navigateTo]);

  const openFile = useCallback(async (entry: FileSystemEntry): Promise<Blob | null> => {
    if (entry.kind !== 'file' || !basePath) return null;
    try {
      const fullPath = efs.joinPath(buildFullDir(state.currentPath), entry.name);
      const ab = efs.readFile(fullPath);
      const ext = getExtension(entry.name);
      const mimeMap: Record<string, string> = {
        pdf: 'application/pdf',
        png: 'image/png',
        jpg: 'image/jpeg',
        jpeg: 'image/jpeg',
        gif: 'image/gif',
        webp: 'image/webp',
        svg: 'image/svg+xml',
        txt: 'text/plain',
        csv: 'text/csv',
        json: 'application/json',
        mp4: 'video/mp4',
        mp3: 'audio/mpeg',
      };
      return new Blob([ab], { type: mimeMap[ext] || 'application/octet-stream' });
    } catch (err: any) {
      setState((prev) => ({ ...prev, error: `Errore nell'apertura del file: ${err.message}` }));
      return null;
    }
  }, [basePath, efs, buildFullDir, state.currentPath]);

  const openFileExternally = useCallback(async (entry: FileSystemEntry) => {
    if (!basePath) return;
    const fullPath = efs.joinPath(buildFullDir(state.currentPath), entry.name);
    await efs.openPath(fullPath);
  }, [basePath, efs, buildFullDir, state.currentPath]);

  const createFolder = useCallback(async (name: string): Promise<boolean> => {
    if (!basePath) return false;
    try {
      const dirPath = efs.joinPath(buildFullDir(state.currentPath), name);
      efs.createDirectory(dirPath);
      updateEntries(state.currentPath);
      return true;
    } catch (err: any) {
      setState((prev) => ({ ...prev, error: `Errore nella creazione della cartella: ${err.message}` }));
      return false;
    }
  }, [basePath, efs, buildFullDir, state.currentPath, updateEntries]);

  const saveFile = useCallback(async (file: File): Promise<boolean> => {
    if (!basePath) return false;
    try {
      const ab = await file.arrayBuffer();
      const filePath = efs.joinPath(buildFullDir(state.currentPath), file.name);
      efs.writeFile(filePath, ab);
      updateEntries(state.currentPath);
      return true;
    } catch (err: any) {
      setState((prev) => ({ ...prev, error: `Errore nel salvataggio del file: ${err.message}` }));
      return false;
    }
  }, [basePath, efs, buildFullDir, state.currentPath, updateEntries]);

  const deleteEntry = useCallback(async (entry: FileSystemEntry): Promise<boolean> => {
    if (!basePath) return false;
    try {
      const entryPath = efs.joinPath(buildFullDir(state.currentPath), entry.name);
      efs.deleteEntry(entryPath);
      updateEntries(state.currentPath);
      return true;
    } catch (err: any) {
      setState((prev) => ({ ...prev, error: `Errore nell'eliminazione: ${err.message}` }));
      return false;
    }
  }, [basePath, efs, buildFullDir, state.currentPath, updateEntries]);

  const refresh = useCallback(async () => {
    updateEntries(state.currentPath);
  }, [state.currentPath, updateEntries]);

  const searchFiles = useCallback(async (query: string): Promise<FileSystemEntry[]> => {
    if (!basePath || !query.trim()) return [];
    const results: FileSystemEntry[] = [];
    const q = query.toLowerCase();

    const searchRecursive = (dirPath: string, relativeSegments: string[]) => {
      try {
        const raw = efs.readDirectory(dirPath);
        for (const e of raw) {
          const entryPath = [...relativeSegments, e.name].join('/');
          if (e.name.toLowerCase().includes(q)) {
            results.push({
              name: e.name,
              kind: e.kind,
              path: entryPath,
              handle: null as unknown as FileSystemHandle,
              size: e.size,
              lastModified: e.lastModified,
              extension: e.kind === 'file' ? getExtension(e.name) : undefined,
            });
          }
          if (e.kind === 'directory') {
            searchRecursive(efs.joinPath(dirPath, e.name), [...relativeSegments, e.name]);
          }
        }
      } catch { /* directory non accessibile */ }
    };

    searchRecursive(basePath, []);
    return results;
  }, [basePath, efs]);

  const selectDirectory = useCallback(async (): Promise<string | null> => {
    return null;
  }, []);

  const requestPermission = useCallback(async (): Promise<boolean> => {
    if (!basePath) return false;
    try {
      return efs.directoryExists(basePath);
    } catch {
      return false;
    }
  }, [basePath, efs]);

  const reloadFromDB = useCallback(async (): Promise<boolean> => {
    if (!basePath) return false;
    try {
      if (efs.directoryExists(basePath)) {
        updateEntries(state.currentPath);
        return true;
      }
    } catch { /* */ }
    return false;
  }, [basePath, efs, state.currentPath, updateEntries]);

  return {
    ...state,
    savedHandleExists: false,
    initialLoadDone,
    selectDirectory,
    navigateTo,
    navigateUp,
    navigateToRoot,
    openFile,
    openFileExternally,
    createFolder,
    saveFile,
    deleteEntry,
    refresh,
    searchFiles,
    requestPermission,
    reloadFromDB,
  };
}
